var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { fetchRecords } from '../api/recordService';
import { isQueryTooShort, MIN_QUERY_WORD_RUN } from '../utils/searchQueryGuard';
import { mclPaging } from '../utils/mclPaging';
var PAGE_SIZE = 50;
/**
 * CZNA-301: the zero UUID is the smallest possible record id. Used as the keyset
 * id-tiebreaker when jumping to a heading: pairing afterSortValue=<typed value>
 * with afterId=ZERO_UUID makes the backend return the first row whose preferred
 * heading is >= the typed value (any real row with that exact heading has an id
 * strictly greater than the zero UUID, so it is included).
 */
var ZERO_UUID = '00000000-0000-0000-0000-000000000000';
/**
 * Display cap for the total-record count. The backend caps the count at this value to
 * keep the query within the gateway timeout (see search-count timeout incident); above
 * it the count is shown as "{cap}+". Keep in sync with TOTAL_COUNT_CAP in
 * RecordServiceImpl.java.
 */
var TOTAL_RECORDS_CAP = 1000;
/**
 * Maps visible column names to backend sortBy parameter values.
 * Only sortable columns are listed here.
 */
var SORT_COLUMN_MAP = {
    headingValue: 'headingValue',
    type: 'type',
    sourceSystem: 'sourceSystem',
    updatedAt: 'updatedAt',
};
/**
 * Maps hook's sort direction ('ascending'/'descending') to backend API values ('asc'/'desc').
 */
var toApiSortOrder = function (direction) {
    return direction === 'ascending' ? 'asc' : 'desc';
};
/**
 * Extracts the sort value from a record item based on the current sort column.
 * The backend uses this value for keyset cursor positioning.
 */
var getSortValue = function (item, apiSortBy) {
    switch (apiSortBy) {
        case 'headingValue':
            return item.headingValue;
        case 'type':
            return item.type;
        case 'sourceSystem':
            return item.sourceSystem;
        case 'updatedAt':
        default:
            return item.updatedAt;
    }
};
/** Root of the list's query key — invalidate it to refetch whatever page is on screen. */
export var RECORDS_QUERY_ROOT = 'czna-authorities-records';
/**
 * Hook for authority records list page with text search, filters, sorting, and keyset pagination.
 * Search is triggered explicitly via handleSearch (form submit / Enter key).
 * Filter state is persisted to URL search params so it survives back-navigation.
 * Pagination uses keyset cursors (afterSortValue/afterId, beforeSortValue/beforeId)
 * while maintaining a numeric pageOffset for MCL display.
 */
var useRecordList = function (_a) {
    var _b, _c, _d, _f;
    var _g = _a === void 0 ? {} : _a, compileFieldQuery = _g.compileFieldQuery;
    var ky = useOkapiKy();
    var location = useLocation();
    var history = useHistory();
    // Parse initial filter values from URL search params (read once on mount)
    var initialParams = useMemo(function () {
        var params = new URLSearchParams(location.search);
        return {
            query: params.get('query') || '',
            type: params.get('type') || null,
            sourceSystem: params.get('sourceSystem') || null,
            status: params.get('status') || 'active',
            sort: params.get('sort') || 'updatedAt',
            order: params.get('order') || 'descending',
            createdDateFrom: params.get('createdDateFrom') || '',
            createdDateTo: params.get('createdDateTo') || '',
            updatedDateFrom: params.get('updatedDateFrom') || '',
            updatedDateTo: params.get('updatedDateTo') || '',
            createdByUserId: params.get('createdBy') || '',
            // TASK-159: repeating validationStatus param → array. URLSearchParams.getAll
            // preserves order; we cast to the narrow union because the values come from
            // a closed enum (any other value is dropped server-side anyway).
            validationStatus: params.getAll('validationStatus'),
            // CZNA-330 (AV-03 drill-down): the issue code arrives via the URL from the
            // corpus-quality dashboard. Not a sidebar control — applied directly,
            // shown as a clearable chip.
            issueCode: params.get('issueCode') || '',
            // CZNA-217: the query line's TEXT, not its compiled JSON — the URL is read and
            // shared by people, and the text is what they wrote.
            fieldQueryText: params.get('fq') || '',
            // CZNA-1400: the checkbox under the query line; written only next to a query.
            fieldQueryMatchCase: params.get('fqcase') === '1',
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    var _h = useState(initialParams.query), query = _h[0], setQuery = _h[1];
    var _j = useState(initialParams.query), appliedQuery = _j[0], setAppliedQuery = _j[1];
    var _k = useState(initialParams.type), typeFilter = _k[0], setTypeFilter = _k[1];
    var _l = useState(initialParams.type), appliedTypeFilter = _l[0], setAppliedTypeFilter = _l[1];
    var _m = useState(initialParams.sourceSystem), sourceSystemFilter = _m[0], setSourceSystemFilter = _m[1];
    var _o = useState(initialParams.sourceSystem), appliedSourceSystemFilter = _o[0], setAppliedSourceSystemFilter = _o[1];
    var _p = useState(initialParams.status), statusFilter = _p[0], setStatusFilter = _p[1];
    var _q = useState(initialParams.status), appliedStatusFilter = _q[0], setAppliedStatusFilter = _q[1];
    var _r = useState(initialParams.sort), sortColumn = _r[0], setSortColumn = _r[1];
    var _s = useState(initialParams.order), sortDirection = _s[0], setSortDirection = _s[1];
    // CZNA-215: whether the user has explicitly chosen a sort column (clicked a column header)
    // or restored one from the URL. When false and a search query is present, the list defaults
    // to relevance ranking so an exact heading/surname match surfaces on top (AC1). Any explicit
    // sort choice overrides relevance. Seeded from the URL: a persisted `sort` param means the
    // user previously chose a sort, so we honour it instead of forcing relevance.
    var _t = useState(new URLSearchParams(location.search).get('sort') !== null), userChoseSort = _t[0], setUserChoseSort = _t[1];
    var _u = useState(0), pageOffset = _u[0], setPageOffset = _u[1];
    var _v = useState({}), keysetState = _v[0], setKeysetState = _v[1];
    var _w = useState(true), filterPaneIsVisible = _w[0], setFilterPaneIsVisible = _w[1];
    // CZNA-533: read back only while it still describes the current filters — see the type.
    var _x = useState(null), countForResultSet = _x[0], setCountForResultSet = _x[1];
    // CZNA-301: "jump to heading" input (pending text the user types into the
    // jump-to control in list/search mode). Applied via jumpToHeading.
    var _y = useState(''), jumpInput = _y[0], setJumpInput = _y[1];
    // Date filter state (pending)
    var _z = useState(initialParams.createdDateFrom), createdDateFrom = _z[0], setCreatedDateFrom = _z[1];
    var _0 = useState(initialParams.createdDateTo), createdDateTo = _0[0], setCreatedDateTo = _0[1];
    var _1 = useState(initialParams.updatedDateFrom), updatedDateFrom = _1[0], setUpdatedDateFrom = _1[1];
    var _2 = useState(initialParams.updatedDateTo), updatedDateTo = _2[0], setUpdatedDateTo = _2[1];
    // Date filter state (applied)
    var _3 = useState(initialParams.createdDateFrom), appliedCreatedDateFrom = _3[0], setAppliedCreatedDateFrom = _3[1];
    var _4 = useState(initialParams.createdDateTo), appliedCreatedDateTo = _4[0], setAppliedCreatedDateTo = _4[1];
    var _5 = useState(initialParams.updatedDateFrom), appliedUpdatedDateFrom = _5[0], setAppliedUpdatedDateFrom = _5[1];
    var _6 = useState(initialParams.updatedDateTo), appliedUpdatedDateTo = _6[0], setAppliedUpdatedDateTo = _6[1];
    // Creator filter state
    var _7 = useState(initialParams.createdByUserId), createdByUserId = _7[0], setCreatedByUserId = _7[1];
    var _8 = useState(''), createdByUserName = _8[0], setCreatedByUserName = _8[1];
    var _9 = useState(initialParams.createdByUserId), appliedCreatedByUserId = _9[0], setAppliedCreatedByUserId = _9[1];
    // TASK-159: validation status filter (sidebar checkboxes). `validationStatus`
    // is the pending state (user-edits), `appliedValidationStatus` is what's
    // currently in flight to the backend.
    var _10 = useState(initialParams.validationStatus), validationStatusFilter = _10[0], setValidationStatusFilter = _10[1];
    var _11 = useState(initialParams.validationStatus), appliedValidationStatusFilter = _11[0], setAppliedValidationStatusFilter = _11[1];
    // CZNA-330 (AV-03 drill-down): issue-code filter from the dashboard. Applied
    // directly from the URL; the user can only clear it (via the chip), not edit it.
    var _12 = useState(initialParams.issueCode), appliedIssueCode = _12[0], setAppliedIssueCode = _12[1];
    var _13 = useState(initialParams.fieldQueryText), fieldQueryText = _13[0], setFieldQueryText = _13[1];
    var _14 = useState(initialParams.fieldQueryText), appliedFieldQueryText = _14[0], setAppliedFieldQueryText = _14[1];
    var _15 = useState(initialParams.fieldQueryMatchCase), fieldQueryMatchCase = _15[0], setFieldQueryMatchCase = _15[1];
    var _16 = useState(initialParams.fieldQueryMatchCase), appliedFieldQueryMatchCase = _16[0], setAppliedFieldQueryMatchCase = _16[1];
    /**
     * CZNA-217: compiled from the APPLIED text, so a deep link (`?fq=…`) runs the same
     * conditions a submit would. `blocked` holds the list back while the catalog loads
     * (sending the other filters alone would flash a wrong result) and when the text has
     * errors (the line marks them; nothing is run).
     */
    var appliedFieldQuery = useMemo(function () {
        if (appliedFieldQueryText.trim() === '') {
            return { tree: undefined, blocked: false };
        }
        var compiled = compileFieldQuery === null || compileFieldQuery === void 0 ? void 0 : compileFieldQuery(appliedFieldQueryText, appliedFieldQueryMatchCase);
        if (!compiled || compiled.hasErrors) {
            return { tree: undefined, blocked: true };
        }
        return { tree: compiled.tree, blocked: false };
    }, [appliedFieldQueryText, appliedFieldQueryMatchCase, compileFieldQuery]);
    var clearIssueCode = useCallback(function () {
        setAppliedIssueCode('');
        setPageOffset(0);
        setKeysetState({});
    }, []);
    // Date validation
    var createdDateValidationError = useMemo(function () { return createdDateFrom && createdDateTo && createdDateFrom > createdDateTo
        ? 'ui-czna-authorities.authorities.filter.dateError' : ''; }, [createdDateFrom, createdDateTo]);
    var updatedDateValidationError = useMemo(function () { return updatedDateFrom && updatedDateTo && updatedDateFrom > updatedDateTo
        ? 'ui-czna-authorities.authorities.filter.dateError' : ''; }, [updatedDateFrom, updatedDateTo]);
    /**
     * Applies every pending filter at once. `overrides` carries what the query line
     * submits: its own text, and its bare words as the heading query (which then also
     * shows in the sidebar search box).
     */
    var applyFilters = useCallback(function (overrides) {
        var _a, _b;
        if (overrides === void 0) { overrides = {}; }
        if (createdDateFrom && createdDateTo && createdDateFrom > createdDateTo)
            return;
        if (updatedDateFrom && updatedDateTo && updatedDateFrom > updatedDateTo)
            return;
        var nextQuery = (_a = overrides.query) !== null && _a !== void 0 ? _a : query;
        var nextFieldQueryText = (_b = overrides.fieldQueryText) !== null && _b !== void 0 ? _b : fieldQueryText;
        setQuery(nextQuery);
        setAppliedQuery(nextQuery);
        setFieldQueryText(nextFieldQueryText);
        setAppliedFieldQueryText(nextFieldQueryText);
        setAppliedFieldQueryMatchCase(fieldQueryMatchCase);
        setAppliedTypeFilter(typeFilter);
        setAppliedSourceSystemFilter(sourceSystemFilter);
        setAppliedStatusFilter(statusFilter);
        setAppliedCreatedDateFrom(createdDateFrom);
        setAppliedCreatedDateTo(createdDateTo);
        setAppliedUpdatedDateFrom(updatedDateFrom);
        setAppliedUpdatedDateTo(updatedDateTo);
        setAppliedCreatedByUserId(createdByUserId);
        setAppliedValidationStatusFilter(validationStatusFilter);
        setPageOffset(0);
        setKeysetState({});
        // CZNA-533: no count reset here — the cached count is keyed by the applied
        // filters, so it is dropped by construction the moment they change. Resetting
        // it by hand in five places is what let the stale-latch bug hide.
    }, [query, fieldQueryText, fieldQueryMatchCase, typeFilter, sourceSystemFilter, statusFilter, createdDateFrom, createdDateTo, updatedDateFrom, updatedDateTo, createdByUserId, validationStatusFilter]);
    var handleSearch = useCallback(function (e) {
        e.preventDefault();
        applyFilters();
    }, [applyFilters]);
    /** CZNA-217: the query line's submit — see {@link applyFilters}. */
    var submitFieldQuery = useCallback(function (text, words) {
        applyFilters(__assign({ fieldQueryText: text }, (words !== undefined ? { query: words } : {})));
    }, [applyFilters]);
    // Sync applied filter state to URL search params so filters survive back-navigation
    useEffect(function () {
        var params = new URLSearchParams();
        // CZNA-216 — preserve the rejstřík/search `mode` flag owned by AuthoritiesList.
        // useRecordList rebuilds the search string from scratch, so without this the
        // index-mode flag would be wiped on every filter change / mount.
        var existingMode = new URLSearchParams(location.search).get('mode');
        if (existingMode)
            params.set('mode', existingMode);
        if (appliedQuery)
            params.set('query', appliedQuery);
        if (appliedTypeFilter)
            params.set('type', appliedTypeFilter);
        if (appliedSourceSystemFilter)
            params.set('sourceSystem', appliedSourceSystemFilter);
        if (appliedStatusFilter !== 'active')
            params.set('status', appliedStatusFilter);
        if (sortColumn !== 'updatedAt')
            params.set('sort', sortColumn);
        if (sortDirection !== 'descending')
            params.set('order', sortDirection);
        if (appliedCreatedDateFrom)
            params.set('createdDateFrom', appliedCreatedDateFrom);
        if (appliedCreatedDateTo)
            params.set('createdDateTo', appliedCreatedDateTo);
        if (appliedUpdatedDateFrom)
            params.set('updatedDateFrom', appliedUpdatedDateFrom);
        if (appliedUpdatedDateTo)
            params.set('updatedDateTo', appliedUpdatedDateTo);
        if (appliedCreatedByUserId)
            params.set('createdBy', appliedCreatedByUserId);
        appliedValidationStatusFilter.forEach(function (v) { return params.append('validationStatus', v); });
        if (appliedIssueCode)
            params.set('issueCode', appliedIssueCode);
        if (appliedFieldQueryText)
            params.set('fq', appliedFieldQueryText);
        if (appliedFieldQueryText && appliedFieldQueryMatchCase)
            params.set('fqcase', '1');
        var search = params.toString();
        history.replace({
            search: search ? "?".concat(search) : '',
        });
    }, [appliedQuery, appliedTypeFilter, appliedSourceSystemFilter, appliedStatusFilter, sortColumn, sortDirection, appliedCreatedDateFrom, appliedCreatedDateTo, appliedUpdatedDateFrom, appliedUpdatedDateTo, appliedCreatedByUserId, appliedValidationStatusFilter, appliedIssueCode, appliedFieldQueryText, appliedFieldQueryMatchCase, history]); // eslint-disable-line react-hooks/exhaustive-deps
    var isDefaultFilters = appliedStatusFilter === 'active' && !appliedTypeFilter && !appliedSourceSystemFilter && appliedQuery === '' && !appliedCreatedDateFrom && !appliedCreatedDateTo && !appliedUpdatedDateFrom && !appliedUpdatedDateTo && !appliedCreatedByUserId && appliedValidationStatusFilter.length === 0 && !appliedIssueCode && !appliedFieldQueryText;
    var apiSortBy = SORT_COLUMN_MAP[sortColumn] || 'updatedAt';
    // CZNA-215: when the user has typed a search query and has not explicitly chosen a sort
    // column, rank results by relevance (exact/prefix heading & surname matches first) instead
    // of the updatedAt default — that is the only way AC1's ranking is visible. Filter-only
    // browsing (no query) keeps the existing updatedAt ordering, and any explicit column sort
    // wins. Relevance is offset-paginated server-side (the rank is computed, not a keyset
    // cursor column), so when it is active we page with `offset` and drop the keyset cursors.
    var useRelevance = appliedQuery !== '' && !userChoseSort;
    var effectiveSortBy = useRelevance ? 'relevance' : apiSortBy;
    // Memoize applied filter params for both the list query and export
    var appliedFilterParams = useMemo(function () { return ({
        query: appliedQuery || undefined,
        type: appliedTypeFilter || undefined,
        sourceSystem: appliedSourceSystemFilter || undefined,
        status: appliedStatusFilter,
        createdFrom: appliedCreatedDateFrom ? "".concat(appliedCreatedDateFrom, "T00:00:00Z") : undefined,
        createdTo: appliedCreatedDateTo ? "".concat(appliedCreatedDateTo, "T23:59:59Z") : undefined,
        updatedFrom: appliedUpdatedDateFrom ? "".concat(appliedUpdatedDateFrom, "T00:00:00Z") : undefined,
        updatedTo: appliedUpdatedDateTo ? "".concat(appliedUpdatedDateTo, "T23:59:59Z") : undefined,
        createdBy: appliedCreatedByUserId || undefined,
        validationStatus: appliedValidationStatusFilter.length > 0 ? appliedValidationStatusFilter : undefined,
        issueCode: appliedIssueCode || undefined,
        // CZNA-217: the same compiled conditions go to the list and to both exports.
        fieldQuery: appliedFieldQuery.tree,
        // CZNA-974 — the "Records" screen is the only useRecordList caller, and the backend
        // ignores this outside the plain active/unfiltered search, so it is always sent rather
        // than threaded through as a further pending/applied state pair.
        includeProposals: true,
    }); }, [appliedQuery, appliedTypeFilter, appliedSourceSystemFilter, appliedStatusFilter, appliedCreatedDateFrom, appliedCreatedDateTo, appliedUpdatedDateFrom, appliedUpdatedDateTo, appliedCreatedByUserId, appliedValidationStatusFilter, appliedIssueCode, appliedFieldQuery.tree]);
    // CZNA-537 — `&& pageOffset === 0`, because relevance mode pages by offset and leaves
    // the keyset cursors empty (see fetchMoreData). On the cursor test alone every relevance
    // page looked like the first one, so the count was re-requested on each of them — and
    // with the export-eligibility flag that count is the wider, more expensive one. The
    // cached count keyed on the filter set already survives paging, so page 2 needs neither.
    var isFirstPage = !keysetState.afterSortValue && !keysetState.beforeSortValue
        && pageOffset === 0;
    // CZNA-215: relevance ranking is offset-paginated (no keyset cursor). For the default/
    // explicit-sort paths we keep keyset pagination. The pagination params and the query key
    // diverge accordingly so react-query refetches when the mode changes.
    var paginationParams = useRelevance
        ? { offset: pageOffset }
        : keysetState;
    /**
     * CZNA-533: identity of the current result set for count-caching purposes. Only the
     * applied filters matter — the backend counts through the same specification the data
     * query filters by (`countUpToCap` → `buildSpecification`) and ignores sort order and
     * cursors entirely, so a column click or a jump-to-heading does not invalidate a count.
     */
    var resultSetKey = useMemo(function () { return JSON.stringify(appliedFilterParams); }, [appliedFilterParams]);
    /**
     * CZNA-628: a substring query with no three-character run of word content cannot use the
     * heading trigram index, so the backend now refuses it with a 400 rather than scanning 2.25M
     * headings for 5-14 s and answering with a capped 1000. The list must therefore not send one —
     * it shows the hint instead. Applied to the APPLIED query, not the typed one, because a short
     * query also arrives by deep link (`?query=no`), where there is no submit to intercept.
     */
    var queryTooShort = isQueryTooShort(appliedQuery);
    var _17 = useQuery([RECORDS_QUERY_ROOT, appliedFilterParams, effectiveSortBy, sortDirection, paginationParams], function () { return fetchRecords(ky, __assign(__assign(__assign({}, appliedFilterParams), { sortBy: effectiveSortBy, sortOrder: toApiSortOrder(sortDirection), limit: PAGE_SIZE, includeTotalCount: isFirstPage, 
        // CZNA-537 — asked for exactly when the count is: this list carries the Export CSV
        // action, and the answer has to be in hand before the user opens the menu.
        includeExportEligibility: isFirstPage }), paginationParams)); }, {
        keepPreviousData: true,
        enabled: !queryTooShort && !appliedFieldQuery.blocked,
    }), data = _17.data, isLoading = _17.isLoading, isFetching = _17.isFetching, isPreviousData = _17.isPreviousData, error = _17.error, refetch = _17.refetch;
    // CZNA-533: remember the count together with the result set it was measured for.
    // Only a first-page response carries one — later pages are fetched without
    // `includeTotalCount` and answer with no `totalRecords` at all (CZNA-795) — and `keepPreviousData`
    // means `data` may still belong to the previous filters while the new page is in
    // flight, which `isPreviousData` marks.
    useEffect(function () {
        var _a, _b;
        if (!data || isPreviousData || !isFirstPage)
            return;
        var capped = Boolean(data.totalRecordsCapped);
        setCountForResultSet({
            key: resultSetKey,
            // CZNA-795: an absent total is "not measured", never 0 — the fourth state beside
            // exact 0, exact N and capped, and it must read as unknown like the capped one does.
            exact: capped ? null : ((_a = data.totalRecords) !== null && _a !== void 0 ? _a : null),
            capped: capped,
            // `?? null` and not `Boolean(...)`: an absent field means the backend did not
            // measure it, which is not the same as "export is blocked" (CZNA-537).
            exportable: (_b = data.exportable) !== null && _b !== void 0 ? _b : null,
        });
    }, [data, isPreviousData, isFirstPage, resultSetKey]);
    // Read the remembered count back only while it still describes the current filters.
    var currentCount = (countForResultSet === null || countForResultSet === void 0 ? void 0 : countForResultSet.key) === resultSetKey ? countForResultSet : null;
    /**
     * CZNA-533 — the count shown to the **user**. `null` means we do not know it: either
     * it is still in flight, or the real total is above `TOTAL_RECORDS_CAP` and the backend
     * refused to count further (see `totalRecordsCapped`). Callers must then say so rather
     * than print a number; there is deliberately no number to fall back on here.
     */
    var totalRecords = (_b = currentCount === null || currentCount === void 0 ? void 0 : currentCount.exact) !== null && _b !== void 0 ? _b : null;
    /** True once the real total is known to exceed TOTAL_RECORDS_CAP. */
    var totalRecordsCapped = (_c = currentCount === null || currentCount === void 0 ? void 0 : currentCount.capped) !== null && _c !== void 0 ? _c : false;
    /**
     * CZNA-537 — whether Export CSV over the current filters would go through: true = yes,
     * false = the result set is over the server's export ceiling, null = not known yet
     * (in flight, or the backend did not answer). Callers must treat null as "no basis to
     * block", not as a refusal.
     */
    var exportable = (_d = currentCount === null || currentCount === void 0 ? void 0 : currentCount.exportable) !== null && _d !== void 0 ? _d : null;
    /**
     * CZNA-533 — what drives **MCL's prev/next paging**, never a record count: above the cap
     * the exact total is unknown, so "next" follows `hasNextPage` and is not clamped to the
     * cap. CZNA-1191 — the count is in page coordinates, not the corpus total; why is in
     * `mclPaging`.
     */
    var _18 = useMemo(function () {
        var _a;
        return mclPaging({
            totalRecords: totalRecords,
            hasNextPage: data === null || data === void 0 ? void 0 : data.hasNextPage,
            pageOffset: pageOffset,
            itemCount: ((_a = data === null || data === void 0 ? void 0 : data.items) === null || _a === void 0 ? void 0 : _a.length) || 0,
        });
    }, [totalRecords, data === null || data === void 0 ? void 0 : data.hasNextPage, (_f = data === null || data === void 0 ? void 0 : data.items) === null || _f === void 0 ? void 0 : _f.length, pageOffset]), paginationTotalCount = _18.totalCount, paginationCanGoNext = _18.canGoNext;
    var handleClearSearch = useCallback(function () {
        setQuery('');
        setAppliedQuery('');
        setJumpInput('');
        setPageOffset(0);
        setKeysetState({});
    }, []);
    var handleClearFilters = useCallback(function () {
        setQuery('');
        setAppliedQuery('');
        setFieldQueryText('');
        setAppliedFieldQueryText('');
        setFieldQueryMatchCase(false);
        setAppliedFieldQueryMatchCase(false);
        setTypeFilter(null);
        setAppliedTypeFilter(null);
        setSourceSystemFilter(null);
        setAppliedSourceSystemFilter(null);
        setStatusFilter('active');
        setAppliedStatusFilter('active');
        setCreatedDateFrom('');
        setAppliedCreatedDateFrom('');
        setCreatedDateTo('');
        setAppliedCreatedDateTo('');
        setUpdatedDateFrom('');
        setAppliedUpdatedDateFrom('');
        setUpdatedDateTo('');
        setAppliedUpdatedDateTo('');
        setCreatedByUserId('');
        setAppliedCreatedByUserId('');
        setCreatedByUserName('');
        setJumpInput('');
        setValidationStatusFilter([]);
        setAppliedValidationStatusFilter([]);
        // CZNA-215: clearing filters returns to the relevance-by-default behavior.
        setUserChoseSort(false);
        setPageOffset(0);
        setKeysetState({});
    }, []);
    var toggleValidationStatusFilter = useCallback(function (status) {
        setValidationStatusFilter(function (prev) {
            return prev.includes(status) ? prev.filter(function (s) { return s !== status; }) : __spreadArray(__spreadArray([], prev, true), [status], false);
        });
    }, []);
    var handleTypeFilterChange = useCallback(function (value) {
        setTypeFilter(value);
    }, []);
    var handleSourceSystemFilterChange = useCallback(function (value) {
        setSourceSystemFilter(value);
    }, []);
    var handleStatusFilterChange = useCallback(function (value) {
        setStatusFilter(value);
    }, []);
    var handleSort = useCallback(function (_e, _a) {
        var name = _a.name;
        if (name === sortColumn) {
            setSortDirection(function (prev) { return (prev === 'ascending' ? 'descending' : 'ascending'); });
        }
        else {
            setSortColumn(name);
            setSortDirection('ascending');
        }
        // CZNA-215: an explicit column click opts out of relevance defaulting.
        setUserChoseSort(true);
        setPageOffset(0);
        setKeysetState({});
    }, [sortColumn]);
    var toggleFilterPane = useCallback(function () {
        setFilterPaneIsVisible(function (prev) { return !prev; });
    }, []);
    // CZNA-301: jump-to-heading. Updates the pending jump text only.
    var setJumpValue = useCallback(function (value) { return setJumpInput(value); }, []);
    // CZNA-301: seed the existing keyset cursor so the list jumps to the first
    // authority whose preferred heading is >= the typed value (czna_cs order, handled
    // by the backend COLLATE keyset). This reuses the existing afterSortValue/afterId
    // plumbing — no new API params. We force heading sort ascending so the anchor is
    // meaningful regardless of the current column/direction, and reset the page offset
    // so subsequent prev/next paging scrolls relative to the jumped-to window.
    var jumpToHeading = useCallback(function () {
        var term = jumpInput.trim();
        if (!term)
            return;
        setSortColumn('headingValue');
        setSortDirection('ascending');
        setUserChoseSort(true);
        setPageOffset(0);
        setKeysetState({ afterSortValue: term, afterId: ZERO_UUID });
        // CZNA-533: the count survives a jump on purpose — the filters have not changed,
        // and a jump moves the cursor off the first page, so the count could not be
        // re-fetched. Clearing it here is how the jumped-to list used to end up showing
        // the synthesized paging number as its record count.
    }, [jumpInput]);
    var fetchMoreData = useCallback(function (offset) {
        var items = data === null || data === void 0 ? void 0 : data.items;
        if (!(items === null || items === void 0 ? void 0 : items.length))
            return;
        // CZNA-215: relevance paging is offset-based — just move the offset; the keyset cursor
        // (which has no stable column under a computed rank) is not used in this mode.
        if (useRelevance) {
            setKeysetState({});
            setPageOffset(offset);
            return;
        }
        if (offset > pageOffset) {
            // Next page — use last item's sort value and id as cursor
            var lastItem = items[items.length - 1];
            setKeysetState({
                afterSortValue: getSortValue(lastItem, apiSortBy),
                afterId: lastItem.id,
            });
        }
        else {
            // Previous page — use first item's sort value and id as cursor
            var firstItem = items[0];
            setKeysetState({
                beforeSortValue: getSortValue(firstItem, apiSortBy),
                beforeId: firstItem.id,
            });
        }
        setPageOffset(offset);
    }, [pageOffset, data === null || data === void 0 ? void 0 : data.items, apiSortBy, useRelevance]);
    return {
        query: query,
        setQuery: setQuery,
        pageOffset: pageOffset,
        pageSize: PAGE_SIZE,
        typeFilter: typeFilter,
        // DB-061: the type currently IN FORCE on the grid, not the pending sidebar
        // selection. The source-system facet is scoped by it, so it has to be the applied
        // one — an offering derived from a type the user has not submitted yet would
        // describe a grid that is not on screen. Exposed like `appliedStatusFilter`.
        appliedTypeFilter: appliedTypeFilter,
        handleTypeFilterChange: handleTypeFilterChange,
        sourceSystemFilter: sourceSystemFilter,
        handleSourceSystemFilterChange: handleSourceSystemFilterChange,
        statusFilter: statusFilter,
        appliedStatusFilter: appliedStatusFilter,
        handleStatusFilterChange: handleStatusFilterChange,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
        handleSort: handleSort,
        // CZNA-301: jump-to-heading control (list/search mode only).
        jumpInput: jumpInput,
        setJumpValue: setJumpValue,
        jumpToHeading: jumpToHeading,
        filterPaneIsVisible: filterPaneIsVisible,
        toggleFilterPane: toggleFilterPane,
        handleSearch: handleSearch,
        // CZNA-217: the query line
        fieldQueryText: fieldQueryText,
        setFieldQueryText: setFieldQueryText,
        appliedFieldQueryText: appliedFieldQueryText,
        fieldQueryMatchCase: fieldQueryMatchCase,
        setFieldQueryMatchCase: setFieldQueryMatchCase,
        appliedFieldQueryMatchCase: appliedFieldQueryMatchCase,
        fieldQueryBlocked: appliedFieldQuery.blocked,
        submitFieldQuery: submitFieldQuery,
        handleClearSearch: handleClearSearch,
        handleClearFilters: handleClearFilters,
        isDefaultFilters: isDefaultFilters,
        fetchMoreData: fetchMoreData,
        // CZNA-628: the hint state. `items` is forced empty rather than left to react-query, because
        // `keepPreviousData` would otherwise keep the previous, longer query's rows on screen under a
        // message saying the current one was never run.
        queryTooShort: queryTooShort,
        minQueryWordRun: MIN_QUERY_WORD_RUN,
        items: queryTooShort || appliedFieldQuery.blocked ? [] : ((data === null || data === void 0 ? void 0 : data.items) || []),
        // CZNA-533: two counts, deliberately not one. `totalRecords` is what the user may
        // be told and is `null` when unknown; `paginationTotalCount` is MCL's paging input
        // and is synthetic above the cap. Do not collapse them back together.
        totalRecords: totalRecords,
        totalRecordsCapped: totalRecordsCapped,
        totalRecordsCap: TOTAL_RECORDS_CAP,
        exportable: exportable,
        paginationTotalCount: paginationTotalCount,
        paginationCanGoNext: paginationCanGoNext,
        isLoading: isLoading,
        isFetching: isFetching,
        error: error,
        // CZNA-538: the lists render a real error state now, and an error state without
        // a way out is a dead end — the retry button calls this.
        refetch: refetch,
        // Date filters
        createdDateFrom: createdDateFrom,
        setCreatedDateFrom: setCreatedDateFrom,
        createdDateTo: createdDateTo,
        setCreatedDateTo: setCreatedDateTo,
        updatedDateFrom: updatedDateFrom,
        setUpdatedDateFrom: setUpdatedDateFrom,
        updatedDateTo: updatedDateTo,
        setUpdatedDateTo: setUpdatedDateTo,
        createdDateValidationError: createdDateValidationError,
        updatedDateValidationError: updatedDateValidationError,
        // Creator filter
        createdByUserId: createdByUserId,
        setCreatedByUserId: setCreatedByUserId,
        createdByUserName: createdByUserName,
        setCreatedByUserName: setCreatedByUserName,
        // Applied filter params (for export — includes ISO date conversion)
        appliedFilterParams: appliedFilterParams,
        // TASK-159: validation-status sidebar filter
        validationStatusFilter: validationStatusFilter,
        appliedValidationStatusFilter: appliedValidationStatusFilter,
        toggleValidationStatusFilter: toggleValidationStatusFilter,
        // CZNA-330: AV-03 drill-down issue-code filter (URL-driven, clearable chip)
        appliedIssueCode: appliedIssueCode,
        clearIssueCode: clearIssueCode,
    };
};
export default useRecordList;
